import React, {useContext, useEffect} from 'react';
import {
    DoppeActionType,
    doppeActionTypeResolveSettingsFromAction
} from "../../client-server-common/doppe-action-types/doppe-action-type";
import {DoppeDtoAction} from "../../client-server-common/types/doppe-dto-action";
import {BymoPageViewerMode, useBymoPageViewerContext} from "../bymo-page-viewer-context/bymo-page-viewer-context";
import {useDoppeViewerClientServices} from "../../client/doppe-user-app/doppe-viewer-client-services";
import {IDoppeViewerActionContext, useBymoPageContextProps} from "../../doppe-sdk";
import {assertDefined, assertNotNullable, ObservableValue} from "@wix/devzai-utils-common";
import {DoppeViewerActionContext} from "../doppe-viewer-action-context/doppe-viewer-action-context";
import {ModalsScopeContext, reactCreateMemo, useObservableCalculatedValue} from "@wix/devzai-utils-react";
import {DoppeBiEmptyTargetUrl} from "../../client-server-common";
import {DoppeViewerModalsController} from "../doppe-viewer-modals-controller/doppe-viewer-modals-controller";

export const DoppeViewerActionRenderer = reactCreateMemo(function DoppeViewerActionRenderer<PROPS extends {}> (
    props: DoppeViewerActionRenderer.Props<PROPS>
) {
    const {
        action,
        actionTogglingController,
        fullScreenModeTogglingController,
        actionIndex = -1,
        children
    } = props;

    const viewerContext = useBymoPageViewerContext();
    const clientServices = useDoppeViewerClientServices();
    const bymoPageContextProps = useBymoPageContextProps();

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(action.actionTypeId),
        `Action type with id '${action.actionTypeId}' isn't registered`);

    useEffect(() => {
        if (viewerContext.mode === BymoPageViewerMode.Live) {
            clientServices.viewerAnalyticsClient.logEvent('uouActionViewed', {
                actionId: action.id,
                pageId: viewerContext.bymoPageId,
                hypeUserId: viewerContext.doppeUserId,
                linkId: viewerContext.linkId,
                itemPosition: actionIndex,
                actionType: action.actionTypeId,
                destinationUrl: bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                pageType: viewerContext.pageType,
            });
        }
    }, []);

    const modalsScope = assertNotNullable(useContext(ModalsScopeContext));


    const actionContext = new DoppeViewerActionContext<PROPS>({
        action: {
            ...action,
            settings: doppeActionTypeResolveSettingsFromAction(actionType, action)
        },
        actionIndex: actionIndex,
        clientServices: clientServices,
        viewerContextData: viewerContext,
        bymoPageContextProps: bymoPageContextProps,
        actionTogglingController: actionTogglingController,
        fullScreenModeTogglingController: fullScreenModeTogglingController,
        modalsController: new DoppeViewerModalsController({
            modalsScope: modalsScope
        })
    })

    const observableActionContext = useObservableCalculatedValue(actionContext as IDoppeViewerActionContext<PROPS>);

    return children(actionContext, actionType, observableActionContext)
});

export namespace DoppeViewerActionRenderer {

    export interface Props<PROPS extends {}> {
        action: DoppeDtoAction;
        actionTogglingController?: DoppeViewerActionContext.ActionTogglingController;
        fullScreenModeTogglingController?: DoppeViewerActionContext.FullScreenModeTogglingController;
        actionIndex?: number;
        children: (
            actionContext: IDoppeViewerActionContext<PROPS>,
            actionType: DoppeActionType<any>,
            observableActionContext: ObservableValue<IDoppeViewerActionContext<PROPS>>
        ) => React.ReactElement;
    }
}
